import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, CalendarClock, Car } from "lucide-react";
import { api, formatDate } from "@/lib/api";

export default function Reschedule() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | done | error
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState(null); // date string
  const [wantPrivate, setWantPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // "rescheduled" | "private_transfer_requested"

  useEffect(() => {
    api.get(`/reschedule/${token}`)
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch(() => setState("error"));
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = wantPrivate ? { choice: "private_transfer" } : { choice: "day", date: selected };
      const r = await api.post(`/reschedule/${token}`, body);
      setResult(r.data.status);
      setState("done");
    } catch (e) {
      alert(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = wantPrivate || !!selected;

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-6 py-16" data-testid="reschedule-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <p className="font-display font-extrabold text-xl uppercase tracking-tighter">Immersion Festival</p>
          <p className="text-[11px] uppercase tracking-[0.35em] text-gray-500 mt-2">Iceland · 1–5 October 2026</p>
        </div>

        {state === "loading" && (
          <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-gray-500" size={28} /></div>
        )}

        {state === "error" && (
          <div className="border border-white/10 bg-[#0C0C0E] p-8 text-center" data-testid="reschedule-error">
            <XCircle className="mx-auto text-[#e07a6c] mb-4" size={36} />
            <h1 className="font-display font-bold text-2xl mb-2">Link no longer valid</h1>
            <p className="text-gray-400 text-sm">This rescheduling link has expired or was already used. Please email <a href="mailto:viking@exclusivereykjavik.is" className="text-[#e3c67d]">viking@exclusivereykjavik.is</a> and we'll help.</p>
          </div>
        )}

        {state === "done" && (
          <div className="border border-white/10 bg-[#0C0C0E] p-8 text-center" data-testid="reschedule-done">
            <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={40} />
            {result === "rescheduled" ? (
              <>
                <h1 className="font-display font-bold text-2xl mb-2">You're all set</h1>
                <p className="text-gray-400 text-sm">Your excursion has been moved. We've emailed you a fresh confirmation with your QR pass and updated calendar invite.</p>
              </>
            ) : (
              <>
                <h1 className="font-display font-bold text-2xl mb-2">Private transfer requested</h1>
                <p className="text-gray-400 text-sm">Thanks! You'll keep your original date. Our team will email you a private transfer quote based on your group size shortly.</p>
              </>
            )}
          </div>
        )}

        {state === "ready" && info && (
          <div className="border border-white/10 bg-[#0C0C0E] p-7" data-testid="reschedule-ready">
            <div className="flex items-center gap-2 text-[#c9a24b] mb-3"><CalendarClock size={16} /><p className="text-[11px] uppercase tracking-[0.25em]">A change to your excursion</p></div>
            <h1 className="font-display font-bold text-2xl mb-4 leading-tight">Please choose a new day for your excursion</h1>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">{info.note}</p>

            <div className="bg-black/40 border border-white/10 p-4 mb-6 text-sm">
              <div className="flex justify-between py-1"><span className="text-gray-500">Excursion</span><span className="text-white font-semibold">{info.booking.tour_name}</span></div>
              <div className="flex justify-between py-1"><span className="text-gray-500">Currently</span><span className="text-white font-semibold">{info.booking.current_date_label}</span></div>
              {info.booking.party > 0 && <div className="flex justify-between py-1"><span className="text-gray-500">Guests</span><span className="text-white font-semibold">{info.booking.party}</span></div>}
            </div>

            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-3">Pick one of the days we can offer</p>
            <div className="space-y-2.5 mb-6">
              {info.options.map((o) => {
                const active = !wantPrivate && selected === o.date;
                return (
                  <button
                    key={o.date}
                    data-testid={`reschedule-option-${o.date}`}
                    onClick={() => { setSelected(o.date); setWantPrivate(false); }}
                    className={`w-full text-left px-4 py-3.5 border transition-all ${active ? "border-[#c9a24b] bg-[#c9a24b]/10" : "border-white/15 hover:border-white/40"}`}
                  >
                    <span className="text-white font-semibold">{o.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="border-t border-white/10 pt-5 mb-6">
              <label data-testid="reschedule-private-toggle" className={`flex items-start gap-3 cursor-pointer px-4 py-3.5 border transition-all ${wantPrivate ? "border-[#c9a24b] bg-[#c9a24b]/10" : "border-white/15 hover:border-white/40"}`}>
                <input type="checkbox" checked={wantPrivate} onChange={(e) => { setWantPrivate(e.target.checked); if (e.target.checked) setSelected(null); }} className="mt-1 w-4 h-4 accent-[#c9a24b]" />
                <span className="flex items-start gap-2">
                  <Car size={16} className="text-[#c9a24b] mt-0.5 shrink-0" />
                  <span className="text-sm text-gray-300">I'd rather keep my date (<strong className="text-white">{info.booking.current_date_label}</strong>) — please send me a <strong className="text-white">private transfer</strong> quote for my group.</span>
                </span>
              </label>
            </div>

            <button
              data-testid="reschedule-submit"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="w-full bg-[#c9a24b] text-black uppercase tracking-[0.15em] text-xs font-bold py-4 transition-all disabled:opacity-40 inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : wantPrivate ? "Request private transfer quote" : "Confirm my new day"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
